import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { AngularFireDatabase, AngularFireAction, DatabaseSnapshot } from '@angular/fire/database';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { filter, map, first } from 'rxjs/operators';
import { ThirdPartiesShared } from '@models/ThirdPartiesShared';
import { ThirdPartiesAccount } from '@models/ThirdPartiesAccount';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ThirdPartiesService {

  constructor(
    private authService: AuthService,
    private afdb: AngularFireDatabase,
    private http: HttpClient
  ) { }

  checkAllAccountArchives() {
    return this.http.post(environment.endPoints.changethirdparty, {
      token: this.authService.currentUser.token,
      action: "checkPendingArchives"
    }).pipe(first()).toPromise();
  }

  fillAccountIds(accountsSnapList: AngularFireAction<DatabaseSnapshot<ThirdPartiesAccount>>[]) {
    return accountsSnapList.map(aSnap => {
      const ac = aSnap.payload.val();
      ac.id = aSnap.key;
      return ac;
    });
  }

  getThirdPartiesOpentokCredentials() {
    return this.afdb.object<any>(`third_parties/shared/opentok/credentials`).valueChanges();
  }

  getThirdPartiesSharedAzureAccounts() {
    return this.afdb.list<any>(`third_parties/shared/azure`)
    .snapshotChanges().pipe(map(aSnapList => aSnapList.map(aSnap => {
      const ac = aSnap.payload.val();
      ac.id = aSnap.key;
      return ac;
    })));
  }

  getThirdPartiesAccount(accountId: string) {
    return this.afdb.object<ThirdPartiesAccount>(`third_parties/accounts/${accountId}`).valueChanges();
  }

  getThirdPartiesCreateOpentokFailures() {
    return this.afdb.list<ThirdPartiesAccount>(`third_parties/accounts`, ref => ref.orderByChild('create_opentok/failed').equalTo(true))
    .snapshotChanges().pipe(map(aSnapList => this.fillAccountIds(aSnapList)));
  }

  getThirdPartiesConnectAzureFailures() {
    return this.afdb.list<ThirdPartiesAccount>(`third_parties/accounts`, ref => ref.orderByChild('connect_azure/failed').equalTo(true))
    .snapshotChanges().pipe(map(aSnapList => this.fillAccountIds(aSnapList)));
  }

  changeOpentokAdminAccount(apiKey: string, apiSecret: string) {
    return this.http.post(environment.endPoints.changethirdparty, {
      token: this.authService.currentUser.token,
      action: "changeOpentokAdminAccount",
      opentok_credentials: { account_api_key: apiKey, account_api_secret: apiSecret }
    }).pipe(first()).toPromise();
  }

  addAzureAccount(accountName: string, accountKey: string) {
    return this.http.post(environment.endPoints.changethirdparty, {
      token: this.authService.currentUser.token,
      action: "addAzureAccount",
      azure_credentials: { account_name: accountName, account_key: accountKey }
    }).pipe(first()).toPromise();
  }

  chooseTargetAzureAccount(azureId: string) {
    return this.http.post(environment.endPoints.changethirdparty, {
      token: this.authService.currentUser.token,
      action: "chooseTargetAzureAccount",
      azure_id: azureId
    }).pipe(first()).toPromise();
  }

  deleteAzureAccount(azureId: string) {
    return this.http.post(environment.endPoints.changethirdparty, {
      token: this.authService.currentUser.token,
      action: "deleteAzureAccount",
      azure_id: azureId
    }).pipe(first()).toPromise();
  }

  retryCreateOpentokFailures() {
    return this.http.post(environment.endPoints.changethirdparty, {
      token: this.authService.currentUser.token,
      action: "retryCreateOpentokFailures"
    }).pipe(first()).toPromise();
  }

  retryConnectAzureFailures() {
    return this.http.post(environment.endPoints.changethirdparty, {
      token: this.authService.currentUser.token,
      action: "retryConnectAzureFailures"
    }).pipe(first()).toPromise();
  }

  clearCreateOpentokFailures() {
    return this.http.post(environment.endPoints.changethirdparty, {
      token: this.authService.currentUser.token,
      action: "clearCreateOpentokFailures"
    }).pipe(first()).toPromise();
  }

  clearConnectAzureFailures() {
    return this.http.post(environment.endPoints.changethirdparty, {
      token: this.authService.currentUser.token,
      action: "clearConnectAzureFailures"
    }).pipe(first()).toPromise();
  }
}
